import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { titleVariants } from './title';

/**
 * D1 §M1 "조용한 권위": 구획은 1px 선이 담당한다. 그림자는 예외다.
 *
 * 그래서 기본값은 테두리만이고 그림자는 명시적으로 선택해야 한다.
 * 프로토타입 4,900줄에서 그림자를 쓴 곳은 모달·팝오버·공문서 지면 셋뿐이었다.
 */
export const cardVariants = cva('rounded-card border', {
  variants: {
    surface: {
      /** 기본. 지면 위에 놓인 카드. */
      page: 'bg-surface',
      /** 선반(회색 패널) 위에 놓여 대비가 필요할 때. */
      shelf: 'bg-canvas',
    },
    elevated: {
      /**
       * 진짜로 떠 있어야 할 때만. 목록 안의 카드에는 쓰지 않는다.
       *
       * [D4-06] 그림자를 켜면 테두리를 끈다. 헤어라인과 넓은 확산 그림자를
       * 겹쳐 쓰는 것은 생성 UI의 서명이고, 둘 중 하나면 충분하다 —
       * 가장자리를 그리든 띄우든, 같은 일을 두 번 하지 않는다.
       */
      true: 'shadow-card border-transparent',
      false: 'border-line',
    },
    interactive: {
      true: cn(
        'ease-flow duration-fast cursor-pointer text-left transition-colors',
        'hover:border-line-strong',
        // D1 §5: 선택은 그림자가 아니라 선이 두꺼워지는 표현이다.
        'aria-[current=true]:border-line-accent aria-[current=true]:ring-line-accent aria-[current=true]:ring-1',
      ),
      false: '',
    },
  },
  defaultVariants: { surface: 'page', elevated: false, interactive: false },
});

export interface CardProps extends ComponentProps<'div'>, VariantProps<typeof cardVariants> {
  /** 제목으로 이름 붙인 구획이면 `section`, 독립된 항목이면 `article`. */
  as?: 'div' | 'section' | 'article';
}

export function Card({
  className,
  surface,
  elevated,
  interactive,
  as: Tag = 'div',
  ...props
}: CardProps) {
  return (
    <Tag className={cn(cardVariants({ surface, elevated, interactive }), className)} {...props} />
  );
}

export interface CardHeaderProps extends ComponentProps<'div'> {
  /** 제목 옆 오른쪽에 붙는 동작 — 목록으로 가는 링크나 버튼 한 개. */
  action?: ReactNode;
}

export function CardHeader({ className, action, children, ...props }: CardHeaderProps) {
  if (action === undefined) {
    return (
      <div className={cn('p-card pb-snug flex flex-col gap-1', className)} {...props}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'p-card pb-snug gap-card flex flex-row items-center justify-between',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1">{children}</div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

export interface CardTitleProps extends ComponentProps<'h3'> {
  as?: 'h1' | 'h2' | 'h3' | 'h4';
}

export function CardTitle({ className, children, as: Tag = 'h3', ...props }: CardTitleProps) {
  return (
    <Tag className={cn(titleVariants({ level: 'card' }), className)} {...props}>
      {children}
    </Tag>
  );
}

export function CardDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-ink-muted leading-body text-sm', className)} {...props} />;
}

export function CardBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('px-card pb-card first:pt-card', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('border-line gap-snug px-card py-row flex items-center border-t', className)}
      {...props}
    />
  );
}
